import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Gears } from './icons';

const TASK_UPDATE_INTERVAL = 5000;

/**
 * Modal with list of tasks that a given user has in queue.
 */
export default function TaskModal({ backendUrl }) {
  const modalBox = useRef(null);
  const [tasks, setTasks] = useState(null);
  const [status, setStatus] = useState('default');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.addEventListener(
      'CALCULATION_SUBMITED',
      handleCalculationSubmited,
    );
    fetchTasks();
    setInterval(fetchTasks, TASK_UPDATE_INTERVAL);
  }, []);

  async function fetchTasks() {
    const resp = await fetch(backendUrl);
    if (300 > resp.status && resp.status >= 200) {
      const data = await resp.json();
      setTasks(data);
    }
    setIsLoading(false);
  }

  function handleCalculationSubmited(evt) {
    if (
      parseInt(evt.detail.status) < 300 &&
      parseInt(evt.detail.status) >= 200
    ) {
      setStatus('parameters_sent');
    } else {
      setStatus('error');
    }

    modalBox.current?.showModal();
    setIsLoading(true);
    fetchTasks();
  }

  return (
    <div
      className="fixed bottom-4 right-4 xl:right-[calc(50%-1280px/2-0.5rem)] sm:bottom-8 sm:right-8 border border-black rounded-md shadow-lg hover:animate-[scale_150ms_ease-in-out_forwards] select-none cursor-pointer bg-base-100 z-50"
      role="complementary"
      aria-label="Panel obliczeń"
    >
      <button
        className="px-3 sm:px-8 py-2 flex justify-center items-center w-full bg-transparent border-none cursor-pointer gap-2"
        onClick={() => {
          modalBox?.current?.showModal();
        }}
        aria-label="Otwórz panel obliczeń"
      >
        <span className="hidden sm:inline">Obliczenia</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="hidden sm:block size-6"
        >
          <path
            fillRule="evenodd"
            d="M11.47 7.72a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 1 1-1.06 1.06L12 9.31l-6.97 6.97a.75.75 0 0 1-1.06-1.06l7.5-7.5Z"
            clipRule="evenodd"
          />
        </svg>
        <Gears className="sm:hidden size-6" />
      </button>
      <dialog ref={modalBox} className="modal">
        <div className="modal-box w-full max-w-lg sm:max-w-xl md:max-w-screen-md">
          <h2 className="text-xl sm:text-2xl font-bold">Twoje obliczenia</h2>
          <p className="my-2">
            Zadania wymagające więcej czasu do przeprowadzenia zostaną
            umieszczone w tej kolejce.
          </p>
          <div className="hidden md:block overflow-x-auto">
            <table className="table mt-4 table-fixed w-full" id="modal-table">
              <thead>
                <tr>
                  <th className="pr-0 bg-gray-50 text-gray-900 w-[15%]">
                    Numer w kolejce
                  </th>
                  <th className="text-center bg-gray-50 text-gray-900 w-[25%]">
                    Status
                  </th>
                  <th className="text-center bg-gray-50 text-gray-900 w-[20%]">
                    Typ zadania
                  </th>
                  <th className="text-center bg-gray-50 text-gray-900 w-[20%]">
                    Utworzono
                  </th>
                  <th className="text-right bg-gray-50 text-gray-900 w-[20%]">
                    Przewidywany czas
                    <br /> zakończenia
                  </th>
                </tr>
              </thead>
              <tbody className="relative min-h-[3lh]">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      <span className="loading loading-bars loading-md"></span>
                    </td>
                  </tr>
                ) : tasks?.length > 0 ? (
                  tasks.map(
                    ({
                      queue_number,
                      status: taskStatus,
                      created_at,
                      task_type,
                      estimated_finish_in,
                    }) => {
                      return (
                        <tr
                          className={clsx({
                            'last:font-bold': status == 'parameters_sent',
                          })}
                          key={`no${queue_number}`}
                        >
                          <td>{queue_number}</td>
                          <td className="text-center min-w-[120px]">
                            <span
                              className={clsx(
                                'inline-block p-2 rounded-md text-xs leading-tight whitespace-nowrap',
                                {
                                  'bg-success text-success-content':
                                    taskStatus.value == 'started',
                                  'bg-secondary text-secondary-content':
                                    taskStatus.value == 'pending',
                                },
                              )}
                            >
                              {taskStatus.display}
                            </span>
                          </td>
                          <td className="text-center">{task_type}</td>
                          <td className="text-center">
                            {trimDateTime(created_at)}
                          </td>
                          <td className="text-end">
                            {estimated_finish_in}
                            min
                          </td>
                        </tr>
                      );
                    },
                  )
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      Obecnie brak zadań w kolejce
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="md:hidden mt-4 space-y-4" aria-live="polite">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <span className="loading loading-bars loading-md"></span>
              </div>
            ) : tasks?.length > 0 ? (
              tasks.map(
                (
                  {
                    queue_number,
                    status: taskStatus,
                    created_at,
                    task_type,
                    estimated_finish_in,
                  },
                  index,
                ) => (
                  <article
                    key={`mobile-${queue_number}`}
                    className={clsx(
                      'rounded-xl border border-base-300 bg-base-100 p-4 shadow-sm',
                      status === 'parameters_sent' &&
                        index === tasks.length - 1 &&
                        'ring-1 ring-secondary',
                    )}
                  >
                    <header className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-base-content/60">
                          Numer w kolejce
                        </p>
                        <p className="text-lg font-semibold">{queue_number}</p>
                      </div>
                      <span
                        className={clsx(
                          'inline-flex items-center rounded-md px-3 py-1 text-xs font-semibold capitalize shadow-sm',
                          {
                            'bg-success text-success-content':
                              taskStatus.value === 'started',
                            'bg-secondary text-secondary-content':
                              taskStatus.value === 'pending',
                          },
                        )}
                      >
                        {taskStatus.display}
                      </span>
                    </header>
                    <dl className="mt-3 grid grid-cols-1 gap-3 text-sm">
                      <div className="flex justify-between gap-4">
                        <dt className="text-base-content/60">Typ zadania</dt>
                        <dd className="font-medium text-right">{task_type}</dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-base-content/60">Utworzono</dt>
                        <dd className="font-medium text-right">
                          {trimDateTime(created_at)}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-base-content/60">
                          Przewidywany czas zakończenia
                        </dt>
                        <dd className="font-medium text-right">
                          {estimated_finish_in} min
                        </dd>
                      </div>
                    </dl>
                  </article>
                ),
              )
            ) : (
              <p className="text-center text-sm text-base-content/70">
                Obecnie brak zadań w kolejce
              </p>
            )}
          </div>
          <p
            className={clsx(
              'whitespace-pre-wrap mt-6 mb-6 text-center text-sm sm:text-base font-semibold animate-[pulse]',
              status == 'error' && 'text-error',
            )}
          >
            {status == 'parameters_sent' ? (
              <span>
                Zadanie dodane do kolejki. <br></br>
                <span>
                  Po zakończeniu obliczeń otrzymasz informacje zwrotną na email.
                </span>
              </span>
            ) : status == 'error' ? (
              'Osiągnięto limit zadań dla aplikacji. Poczekaj na zakończenie któregoś.'
            ) : (
              'Po zakończeniu obliczeń otrzymasz informacje zwrotną na email.'
            )}
          </p>
          <div className="modal-action mt-0 flex-col-reverse gap-3 sm:flex-row sm:items-center">
            <form method="dialog" className="w-full sm:w-auto">
              <button
                className="btn btn-secondary w-full"
                onClick={() => setStatus('default')}
              >
                Zamknij
              </button>
            </form>
          </div>
        </div>
      </dialog>
    </div>
  );
}

function trimDateTime(isoString) {
  // trim mili and seconds
  let d = new Date(isoString);
  return (
    d.toLocaleDateString() +
    ' ' +
    d.toLocaleTimeString(navigator.language, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  );
}

export function triggerModalUpdate(detail) {
  const eventSubmit = new CustomEvent('CALCULATION_SUBMITED', {
    detail,
  });
  document.dispatchEvent(eventSubmit);
}
